import React, { useState, useEffect, useMemo, useRef } from 'react';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import '../styles/nav.css';
import Logo from '../assets/LogoIni.png';
import Carrito from '../assets/carrito.png';
import { getAllProducts, getAllCombos } from '../services/catalogApi';
import { productoPath } from '../utils/slug';
import { Search } from 'lucide-react';


const normalizeText = (value) =>
  String(value || '')
    .trim()
    .toLowerCase()
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '');

const SUGERENCIAS_MAX = 8;

const Nav = () => {
  const [cantidadCarrito, setCantidadCarrito] = useState(0);
  const [menuAbierto, setMenuAbierto] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [sugerenciasAbiertas, setSugerenciasAbiertas] = useState(false);
  const [catalogo, setCatalogo] = useState({ productos: [], combos: [] });
  const iconRef = useRef(null);
  const menuRef = useRef(null);
  const toggleRef = useRef(null);
  const searchRef = useRef(null);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    Promise.all([getAllProducts(), getAllCombos()])
      .then(([productos, combos]) => setCatalogo({ productos, combos }))
      .catch(() => setCatalogo({ productos: [], combos: [] }));
  }, []);

  const sugerencias = useMemo(() => {
    const termino = normalizeText(searchTerm);
    if (termino.length < 2) return [];

    const productos = catalogo.productos
      .filter((item) => normalizeText(item.artNom).includes(termino))
      .map((item) => ({
        id: `producto-${item.artId}`,
        tipo: 'Producto',
        nombre: item.artNom,
        imagen: item.artFoto?.[0],
        ruta: productoPath(item),
      }));

    const combos = catalogo.combos
      .filter((item) => normalizeText(item.comboNombre).includes(termino))
      .map((item) => ({
        id: `combo-${item.comboId}`,
        tipo: 'Combo',
        nombre: item.comboNombre,
        imagen: item.comboFoto?.[0],
        ruta: `/combos/${item.comboId}`,
      }));

    return [...productos, ...combos].slice(0, SUGERENCIAS_MAX);
  }, [searchTerm, catalogo]);

  useEffect(() => {
    const carrito = JSON.parse(localStorage.getItem('carrito')) || [];
    window.dispatchEvent(new Event('carrito-actualizado'));
    setCantidadCarrito(carrito.length);
  }, []);

  useEffect(() => {
    const handleStorageChange = () => {
      const carrito = JSON.parse(localStorage.getItem('carrito')) || [];
      setCantidadCarrito(carrito.length);

      if (iconRef.current) {
        iconRef.current.classList.remove('saltar');
        void iconRef.current.offsetWidth;
        iconRef.current.classList.add('saltar');
      }
    };

    window.addEventListener('carrito-actualizado', handleStorageChange);
    return () => {
      window.removeEventListener('carrito-actualizado', handleStorageChange);
    };
  }, []);

  useEffect(() => {
    if (!sugerenciasAbiertas) return undefined;

    const handleOutside = (event) => {
      if (searchRef.current && !searchRef.current.contains(event.target)) {
        setSugerenciasAbiertas(false);
      }
    };

    document.addEventListener('mousedown', handleOutside);
    document.addEventListener('touchstart', handleOutside);
    return () => {
      document.removeEventListener('mousedown', handleOutside);
      document.removeEventListener('touchstart', handleOutside);
    };
  }, [sugerenciasAbiertas]);

  useEffect(() => {
    if (!menuAbierto) return undefined;

    const handleOutside = (event) => {
      const target = event.target;
      if (
        menuRef.current
        && !menuRef.current.contains(target)
        && toggleRef.current
        && !toggleRef.current.contains(target)
      ) {
        setMenuAbierto(false);
      }
    };

    document.addEventListener('mousedown', handleOutside);
    document.addEventListener('touchstart', handleOutside);
    return () => {
      document.removeEventListener('mousedown', handleOutside);
      document.removeEventListener('touchstart', handleOutside);
    };
  }, [menuAbierto]);

  const toggleMenu = () => setMenuAbierto((prev) => !prev);

  const handleLinkClick = () => {
    if (menuAbierto) setMenuAbierto(false);
  };

  const irAInicio = () => {
    navigate('/');
    handleLinkClick();
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const scrollToContacto = () => {
    if (location.pathname !== '/') {
      navigate('/');
      setTimeout(() => {
        const contacto = document.getElementById('contactanos');
        if (contacto) {
          const yOffset = -120;
          const y = contacto.getBoundingClientRect().top + window.pageYOffset + yOffset;
          window.scrollTo({ top: y, behavior: 'smooth' });
        }
      }, 120);
      handleLinkClick();
      return;
    }

    const contacto = document.getElementById('contactanos');
    if (contacto) {
      const yOffset = -120;
      const y = contacto.getBoundingClientRect().top + window.pageYOffset + yOffset;
      window.scrollTo({ top: y, behavior: 'smooth' });
    }
    handleLinkClick();
  };

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    const query = searchTerm.trim();
    navigate(query ? `/tienda?q=${encodeURIComponent(query)}` : '/tienda');
    setSearchTerm('');
    setSugerenciasAbiertas(false);
    handleLinkClick();
  };

  const handleSugerenciaClick = (sugerencia) => {
    navigate(sugerencia.ruta);
    setSearchTerm('');
    setSugerenciasAbiertas(false);
    handleLinkClick();
  };

  return (
    <nav className="nav" aria-label="Navegacion principal">
      <div className="nav-container">
        <button type="button" className="nav-brand-btn" onClick={irAInicio} aria-label="Ir al inicio">
          <img src={Logo} className="logo-ini" alt="CleanZone" />
        </button>

        <div ref={menuRef} className={`nav-right ${menuAbierto ? 'open' : ''}`}>
          <div className="nav-search-wrap" ref={searchRef}>
            <form className="nav-search" onSubmit={handleSearchSubmit} role="search" aria-label="Buscar productos">
              <input
                type="search"
                value={searchTerm}
                onChange={(e) => {
                  setSearchTerm(e.target.value);
                  setSugerenciasAbiertas(true);
                }}
                onFocus={() => setSugerenciasAbiertas(true)}
                placeholder="Buscar producto..."
                className="nav-search-input"
                autoComplete="off"
              />
              <button type="submit" className="nav-search-btn" aria-label="Buscar">
                <Search size={16} strokeWidth={2.4} aria-hidden="true" />
              </button>
            </form>
            {sugerenciasAbiertas && sugerencias.length > 0 && (
              <ul className="nav-search-suggestions" role="listbox">
                {sugerencias.map((sugerencia) => (
                  <li key={sugerencia.id} role="option" aria-selected="false">
                    <button
                      type="button"
                      className="nav-search-suggestion"
                      onClick={() => handleSugerenciaClick(sugerencia)}
                    >
                      {sugerencia.imagen && (
                        <img src={sugerencia.imagen} alt={sugerencia.nombre} className="nav-search-suggestion-img" />
                      )}
                      <span className="nav-search-suggestion-info">
                        <span className="nav-search-suggestion-nombre">{sugerencia.nombre}</span>
                        <span className="nav-search-suggestion-tipo">{sugerencia.tipo}</span>
                      </span>
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
          <ul className="nav-links">
            <li><button type="button" className="nav-link-button" onClick={irAInicio}>Inicio</button></li>
            <li><button type="button" className="nav-link-button" onClick={scrollToContacto}>Contacto</button></li>
          </ul>
          <NavLink to="/tienda" className="nav-cta" onClick={handleLinkClick}>Ir a tienda</NavLink>
        </div>

        <div className="nav-icons">
          <NavLink to="/carrito" className="carrito-icono" onClick={handleLinkClick} aria-label="Ir al carrito">
            <img src={Carrito} alt="Carrito" className="icono-img" ref={iconRef} />
            {cantidadCarrito > 0 && <span className="carrito-badge">{cantidadCarrito}</span>}
          </NavLink>

          <button ref={toggleRef} className="menu-toggle" onClick={toggleMenu} aria-label="Abrir menu">
            <span />
            <span />
            <span />
          </button>
        </div>
      </div>
      {menuAbierto && (
        <button
          type="button"
          className="nav-backdrop nav-backdrop-show"
          onClick={handleLinkClick}
          aria-label="Cerrar menu"
        />
      )}
    </nav>
  );
};

export default Nav;
